<template>
  <div class="cdk-page">
    <header class="page-header">
      <button class="header-button back-button" type="button" aria-label="返回" @click="goBack">‹</button>
      <h1>兑换详情</h1>
      <button class="header-button help-button" type="button" @click="helpVisible = true">帮助</button>
    </header>

    <main class="page-content">
      <section v-if="initialLoading" class="state-card">
        <span class="loading-spinner"></span>
        <p>正在获取兑换信息...</p>
      </section>

      <section v-else-if="!valid" class="state-card">
        <div class="state-icon">!</div>
        <h2>{{ requestFailed ? "加载失败" : "链接不可用" }}</h2>
        <p>{{ message }}</p>
        <button v-if="requestFailed" class="primary-button" type="button" @click="retry">重新加载</button>
      </section>

      <template v-else>
        <section class="summary-card">
          <h2>订单信息</h2>
          <div v-if="paymentTradeSn" class="summary-row"><span>抖店订单</span><strong>{{ paymentTradeSn }}</strong></div>
          <div class="summary-row"><span>发放角色</span><strong>{{ roleText }}</strong></div>
          <div class="summary-row"><span>兑换数量</span><strong>{{ items.length }} 个</strong></div>
        </section>

        <section v-if="!items.length" class="state-card">
          <span class="loading-spinner"></span>
          <h2>兑换码生成中</h2>
          <p>兑换码生成后将自动核销并发放道具，请稍候。</p>
        </section>

        <section v-else>
          <article v-for="(item, index) in items" :key="item.order_sn || index" class="cdk-card">
            <div class="card-heading">
              <strong>兑换码 {{ index + 1 }}</strong>
              <span :class="['status-tag', 'status-' + item.status]">{{ item.status_name }}</span>
            </div>
            <div class="code-box">
              <span class="code-value">{{ item.cdkey }}</span>
              <button class="copy-button" type="button" @click="copyCdkey(item.cdkey)">复制</button>
            </div>
            <div class="progress-bar" aria-label="兑换进度">
              <div v-for="(step, stepIndex) in progressSteps" :key="step" class="progress-step">
                <span v-if="stepIndex" :class="['step-line', { complete: isStepComplete(item, stepIndex) }]"></span>
                <span :class="['step-dot', { complete: isStepComplete(item, stepIndex), active: isStepActive(item, stepIndex) }]">
                  <span v-if="isStepComplete(item, stepIndex)">✓</span>
                </span>
                <span :class="['step-label', { complete: isStepComplete(item, stepIndex) }]">{{ step }}</span>
              </div>
            </div>
            <div class="status-message"><i></i><span>{{ statusDescription(item) }}</span></div>
          </article>
        </section>

        <div class="refresh-area">
          <button v-if="!allArrived" type="button" :disabled="refreshing" @click="manualRefresh">
            <span :class="{ rotating: refreshing }">↻</span>{{ refreshing ? "刷新中" : "刷新状态" }}
          </button>
          <p>{{ allArrived ? "全部道具已发放完成" : "页面每 10 秒自动更新" }}</p>
        </div>
      </template>
    </main>

    <div v-if="helpVisible" class="modal-mask" @click.self="helpVisible = false">
      <section class="help-modal">
        <button class="modal-close" type="button" aria-label="关闭" @click="helpVisible = false">×</button>
        <h2>兑换说明</h2>
        <p>兑换码生成后，系统会自动完成核销并向对应游戏角色发放道具，无需手动操作。</p>
        <p>如状态暂未更新，请保持页面打开或稍后重新进入查看。</p>
        <button class="primary-button" type="button" @click="helpVisible = false">我知道了</button>
      </section>
    </div>
  </div>
</template>

<script>
import { Toast } from "vant";
import { getDoudianWechatCdk } from "@/apis/gameJump";

const POLL_INTERVAL = 10000;

export default {
  name: "DoudianWechatCdk",
  data() {
    return {
      initialLoading: true,
      refreshing: false,
      polling: false,
      valid: false,
      hasLoaded: false,
      requestFailed: false,
      message: "链接无效或订单暂不可查看",
      paymentTradeSn: "",
      roleName: "",
      server: null,
      serverName: "",
      items: [],
      pollTimer: null,
      helpVisible: false,
      progressSteps: ["兑换码已生成", "已核销", "道具已到账"],
    };
  },
  computed: {
    serverText() {
      if (this.serverName) return this.serverName;
      return this.server !== null && this.server !== undefined ? "S" + this.server : "";
    },
    roleText() {
      if (!this.roleName && !this.serverText) return "待获取";
      if (this.roleName && this.serverText) return this.roleName + " · " + this.serverText;
      return this.roleName || this.serverText;
    },
    allArrived() {
      return this.items.length > 0 && this.items.every((item) => Number(item.status) === 4);
    },
  },
  created() {
    this.loadData({ initial: true });
  },
  mounted() {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.stopPolling();
  },
  methods: {
    async loadData({ initial = false, silent = false } = {}) {
      if (this.polling) return;
      const { session, platform } = this.$route.query;
      if (!session || !["yuewan", "wanxin"].includes(platform)) {
        this.valid = false;
        this.requestFailed = false;
        this.message = "链接参数不完整，请从抖店订单重新进入。";
        this.initialLoading = false;
        this.stopPolling();
        return;
      }
      this.polling = true;
      if (initial) this.initialLoading = true;
      try {
        const response = await getDoudianWechatCdk({ session, platform });
        const content = response && response.content;
        if (!content || !content.valid) {
          this.valid = false;
          this.requestFailed = false;
          this.message = (content && content.message) || "链接无效或订单暂不可查看";
          this.stopPolling();
          return;
        }
        this.valid = true;
        this.hasLoaded = true;
        this.requestFailed = false;
        this.message = content.message || "success";
        this.paymentTradeSn = content.payment_trade_sn || content.paymentTradeSn || "";
        this.roleName = content.role_name || content.roleName || "";
        this.server = content.server;
        this.serverName = content.server_name || content.serverName || "";
        this.items = (content.items || []).map(this.normalizeItem);
      } catch (error) {
        if (this.hasLoaded && silent) {
          Toast("状态更新失败，请稍后重试");
        } else {
          this.valid = false;
          this.requestFailed = true;
          this.message = "网络开小差了，请点击下方按钮重新加载。";
          this.stopPolling();
        }
      } finally {
        this.polling = false;
        this.initialLoading = false;
        this.refreshing = false;
        if (this.valid && !this.allArrived && !document.hidden) this.startPolling();
        if (this.allArrived) this.stopPolling();
      }
    },
    normalizeItem(item) {
      return {
        ...item,
        order_sn: item.order_sn || item.orderSn,
        status_name: item.status_name || item.statusName,
        status_time: item.status_time || item.statusTime,
      };
    },
    retry() {
      this.loadData({ initial: true });
    },
    manualRefresh() {
      if (this.polling) return;
      this.refreshing = true;
      this.loadData({ silent: this.hasLoaded });
    },
    startPolling() {
      if (this.pollTimer || this.allArrived || !this.valid || document.hidden) return;
      this.pollTimer = window.setInterval(() => this.loadData({ silent: true }), POLL_INTERVAL);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    handleVisibilityChange() {
      if (document.hidden) this.stopPolling();
      else if (this.valid && !this.allArrived) this.loadData({ silent: this.hasLoaded });
    },
    goBack() {
      window.history.back();
    },
    async copyCdkey(cdkey) {
      if (!cdkey) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(cdkey);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = cdkey;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        Toast.success("兑换码已复制");
      } catch (error) {
        Toast.fail("复制失败，请长按兑换码复制");
      }
    },
    isStepComplete(item, stepIndex) {
      const status = Number(item.status);
      if (stepIndex === 0) return status >= 0;
      if (stepIndex === 1) return status >= 2;
      return status >= 4;
    },
    isStepActive(item, stepIndex) {
      const status = Number(item.status);
      return (stepIndex === 0 && status <= 1) || (stepIndex === 1 && status >= 2 && status <= 3) || (stepIndex === 2 && status === 4);
    },
    statusDescription(item) {
      const time = item.status_time ? item.status_time + " " : "";
      const descriptions = {
        0: "兑换码已生成，正在等待自动核销",
        1: "正在自动核销，请稍候",
        2: "兑换码已核销，正在准备发放道具",
        3: time + "道具发放处理中，请稍候",
        4: time + "已发放至" + this.roleText,
      };
      return descriptions[Number(item.status)] || item.status_name || "状态更新中";
    },
  },
};
</script>

<style scoped>
.cdk-page {
  --page-bg: #f5f6f8;
  --card-bg: #fff;
  --soft-bg: #f7f8fa;
  --text-main: #1f2329;
  --text-secondary: #646a73;
  --text-muted: #8f959e;
  --border-color: #e8eaed;
  --brand: #fe2c55;
  --brand-soft: #fff0f3;
  --success: #20b26b;
  --warning: #ff8a00;
  min-height: 100vh;
  background: var(--page-bg);
  color: var(--text-main);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.page-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: env(safe-area-inset-top) 16px 0;
  border-bottom: 1px solid var(--border-color);
  background: var(--card-bg);
}

.page-header h1 { margin: 0; font-size: 17px; font-weight: 600; }
.header-button { width: 48px; padding: 0; border: 0; background: transparent; color: var(--text-main); }
.back-button { text-align: left; font-size: 34px; line-height: 1; }
.help-button { text-align: right; font-size: 14px; }
.page-content { box-sizing: border-box; width: 100%; max-width: 430px; min-height: calc(100vh - 53px); margin: auto; padding: 16px 14px calc(28px + env(safe-area-inset-bottom)); }
.summary-card, .cdk-card, .state-card { border: 1px solid var(--border-color); border-radius: 16px; background: var(--card-bg); box-shadow: 0 5px 18px rgba(31, 35, 41, 0.04); }
.summary-card { margin-bottom: 14px; padding: 18px; }
.summary-card h2 { margin: 0 0 14px; font-size: 16px; }
.summary-row { display: flex; justify-content: space-between; gap: 16px; margin-top: 10px; color: var(--text-secondary); font-size: 14px; }
.summary-row strong { overflow: hidden; color: var(--text-main); font-weight: 500; text-align: right; text-overflow: ellipsis; white-space: nowrap; }
.cdk-card { margin-bottom: 14px; padding: 18px 16px; }
.card-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; font-size: 16px; }
.status-tag { padding: 4px 9px; border-radius: 999px; background: var(--brand-soft); color: var(--brand); font-size: 12px; font-weight: 400; }
.status-0, .status-1, .status-3 { background: rgba(255, 138, 0, 0.12); color: var(--warning); }
.status-4 { background: rgba(32, 178, 107, 0.12); color: var(--success); }
.code-box { display: flex; align-items: center; gap: 12px; padding: 14px; border-radius: 12px; background: var(--soft-bg); }
.code-value { flex: 1; overflow-wrap: anywhere; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 17px; font-weight: 700; letter-spacing: 0.5px; }
.copy-button { flex: none; padding: 7px 13px; border: 1px solid var(--brand); border-radius: 16px; background: transparent; color: var(--brand); font-size: 13px; }
.progress-bar { display: flex; margin: 22px 2px 16px; }
.progress-step { position: relative; flex: 1; padding-top: 18px; text-align: center; }
.step-line { position: absolute; top: 6px; right: 50%; width: 100%; height: 2px; background: var(--border-color); }
.step-line.complete { background: var(--brand); }
.step-dot { position: absolute; top: 0; left: 50%; z-index: 1; display: flex; align-items: center; justify-content: center; width: 14px; height: 14px; border: 2px solid var(--border-color); border-radius: 50%; background: var(--card-bg); color: #fff; font-size: 10px; transform: translateX(-50%); }
.step-dot.complete { border-color: var(--brand); background: var(--brand); }
.step-dot.active { box-shadow: 0 0 0 4px var(--brand-soft); }
.step-label { color: var(--text-muted); font-size: 11px; white-space: nowrap; }
.step-label.complete { color: var(--text-main); }
.status-message { display: flex; align-items: flex-start; gap: 8px; padding-top: 13px; border-top: 1px solid var(--border-color); color: var(--text-secondary); font-size: 13px; line-height: 1.5; }
.status-message i { flex: none; width: 6px; height: 6px; margin-top: 7px; border-radius: 50%; background: var(--brand); }
.state-card { padding: 54px 24px; text-align: center; }
.state-card h2 { margin: 16px 0 8px; font-size: 18px; }
.state-card p { margin: 16px 0 0; color: var(--text-secondary); font-size: 14px; line-height: 1.7; }
.state-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: var(--brand-soft); color: var(--brand); font-size: 24px; font-weight: 700; }
.loading-spinner { display: inline-block; width: 30px; height: 30px; border: 3px solid var(--border-color); border-top-color: var(--brand); border-radius: 50%; animation: rotate 0.9s linear infinite; }
.primary-button { min-width: 120px; margin-top: 24px; padding: 11px 24px; border: 0; border-radius: 22px; background: var(--brand); color: #fff; font-size: 14px; }
.refresh-area { padding: 8px 0 4px; text-align: center; }
.refresh-area button { border: 0; background: transparent; color: var(--brand); font-size: 14px; }
.refresh-area button:disabled { opacity: 0.6; }
.refresh-area button span { display: inline-block; margin-right: 4px; font-size: 18px; }
.refresh-area p { margin: 6px 0 0; color: var(--text-muted); font-size: 12px; }
.modal-mask { position: fixed; z-index: 30; inset: 0; display: flex; align-items: flex-end; justify-content: center; padding: 16px; background: rgba(0, 0, 0, 0.48); }
.help-modal { position: relative; box-sizing: border-box; width: 100%; max-width: 430px; padding: 26px 22px calc(22px + env(safe-area-inset-bottom)); border-radius: 20px; background: var(--card-bg); }
.help-modal h2 { margin: 0 0 16px; font-size: 19px; }
.help-modal p { margin: 10px 0; color: var(--text-secondary); font-size: 14px; line-height: 1.7; }
.help-modal .primary-button { width: 100%; }
.modal-close { position: absolute; top: 14px; right: 16px; border: 0; background: transparent; color: var(--text-muted); font-size: 26px; }
.rotating { animation: rotate 0.8s linear infinite; }
@keyframes rotate { to { transform: rotate(360deg); } }

@media (prefers-color-scheme: dark) {
  .cdk-page { --page-bg: #111214; --card-bg: #1b1d20; --soft-bg: #25272b; --text-main: #f2f3f5; --text-secondary: #b7bbc2; --text-muted: #858a93; --border-color: #303238; --brand-soft: rgba(254, 44, 85, 0.15); }
  .summary-card, .cdk-card, .state-card { box-shadow: none; }
}

@media (min-width: 600px) {
  .page-content { padding-top: 28px; }
  .modal-mask { align-items: center; }
}
</style>
